/*
 ************************************************************************
 *  © [2015 - 2023] Quintype Technologies India Private Limited
 *  All Rights Reserved.
 *************************************************************************
 */

import React, { useCallback } from "react";
import { connect } from "react-redux";
import { navigate } from "utils/routes.utils";
import { pick, get } from "lodash";
import { t } from "i18n";
import classnames from "classnames/bind";
import { actions } from "pages/story-editor/actions";
import { STORY_EDITOR_ALTERNATIVES_PATH, STORY_EDITOR_HERO_IMAGE_GALLERY_PATH } from "pages/story-editor/routes";
import { ImageId, StoryId } from "api/primitive-types";
import { Errors } from "pages/story-editor/state";
import { Image as ImageType } from "api/search-media-image";
import { CustomField, Fields } from "api/route-data/story-route-data";
import { Story } from "api/story";
import { getErrorMessage } from "pages/story-editor/utils";
import DoubleStar from "components/icons/double-star";
import Image from "components/icons/story-elements/image";
import Headline from "components/icons/headline";
import SubTitle from "components/icons/sub-title";
import ExternalUrl from "components/icons/external-url";
import TextArea from "components/text-area/text-area";
import TextField from "components/text-field/text-field";
import ReviewFields from "pages/story-editor/header-card/review-header-fields";
import CustomMetadata from "pages/story-editor/components/custom-metadata/custom-metadata";
import HeroImage from "pages/story-editor/components/hero-image/hero-image";
import TextLimitIndicator from "components/text-limit-indicator/text-limit-indicator";
import styles from "./header-card.module.css";
import { getTemplateCustomMetadata } from "./utils";

const cx = classnames.bind(styles);

const defaultHeadlineLimit = 66;
const defaultSubheadlineLimit = 200;

interface StateProps {
  story: Story;
  storyTemplateFields: Fields;
  customMetadataFields: CustomField[];
  errors: Errors;
  isStoryLocked: boolean;
  isAlternativesEnabled: boolean;
  isFocusPointMandatory: boolean;
}

interface DispatchProps {
  onChangeOfStory(key: string, value: any): void;
  openAlternativesInspector(storyId): void;
  updateStoryHeroImageData(image: ImageType): void;
  deleteStoryHeroImage(): void;
  updateMetadata(key: string, value: any): void;
  openPhotoEditor(image: ImageType, imageAs: { type: string; subtype?: string }, imageId: ImageId): void;
  openMediaGallery(storyId): void;
}

interface OwnProps {
  timelineOpenCls: any;
  storyInspectorOpenCls: any;
  previewOpenCls: any;
  seoScoreOpenCls: any;
  readOnlyStoryVersionId: StoryId;
}

type Props = StateProps & DispatchProps & OwnProps;

export const HeaderCard: React.FC<Props> = React.memo(
  ({
    story,
    storyTemplateFields,
    customMetadataFields,
    onChangeOfStory,
    openMediaGallery,
    updateStoryHeroImageData,
    deleteStoryHeroImage,
    updateMetadata,
    errors = {},
    isStoryLocked,
    readOnlyStoryVersionId,
    openPhotoEditor,
    openAlternativesInspector,
    timelineOpenCls,
    previewOpenCls,
    storyInspectorOpenCls,
    seoScoreOpenCls,
    isFocusPointMandatory
  }) => {
    const storyId = story["story-content-id"];
    const storyTemplate = story["story-template"];

    const metadataFields = getTemplateCustomMetadata(customMetadataFields, storyTemplate);

    const customMetadata =
      story.metadata &&
      pick(
        story.metadata,
        metadataFields.map((val) => val["key-name"])
      );
    const handleHeadlineChange = useCallback((headline) => onChangeOfStory("headline", headline), [onChangeOfStory]);
    const handleSubHeadlineChange = useCallback((subheadline) => onChangeOfStory("subheadline", subheadline), [
      onChangeOfStory
    ]);
    const handleReferenceUrlChange = useCallback((value) => updateMetadata("reference-url", value), [updateMetadata]);
    const isReadOnly = !!(isStoryLocked || readOnlyStoryVersionId);

    const storyHeader = cx("story-header", timelineOpenCls, previewOpenCls, seoScoreOpenCls, storyInspectorOpenCls);

    return (
      <section className={storyHeader} data-test-id="editor-header">
        {isReadOnly && <div className={styles["block"]} />}
        <div
          className={cx("story-field-label", "headline", `${errors["headline"] ? "has-error" : ""}`)}
          data-test-id="story-field-label-headline">
          <div className={cx("title-icon", `${errors["headline"] ? "has-error" : ""}`)} data-test-id="title-icon">
            <Headline color="currentColor" />
          </div>
          <TextLimitIndicator
            text={story.headline}
            limit={get(storyTemplateFields, ["headline", "validations", "hard-limit"], defaultHeadlineLimit)}
          />
        </div>

        <div
          className={cx("story-title", "story-title-text-area", isReadOnly ? "story-title-text-area--readonly" : "")}
          data-test-id="story-title-text-area">
          <TextArea
            onChange={handleHeadlineChange}
            placeholder={t("story-editor.header-card.title")}
            value={story.headline}
            errorMessage={getErrorMessage(errors["headline"])}
            variant="plain"
            size="jumbo"
            disabled={isReadOnly}
          />
        </div>

        {!storyTemplateFields.subheadline.validations["hidden"] && (
          <div
            className={cx("story-field-label", "subheadline", `${errors["subheadline"] ? "has-error" : ""}`)}
            data-test-id="story-field-label-subheadline">
            <div className={cx("title-icon", `${errors["subheadline"] ? "has-error" : ""}`)}>
              <SubTitle color="currentColor" />
            </div>

            <TextLimitIndicator
              text={story.subheadline}
              limit={get(storyTemplateFields, ["subheadline", "validations", "hard-limit"], defaultSubheadlineLimit)}
            />
          </div>
        )}
        {!storyTemplateFields.subheadline.validations["hidden"] && (
          <div
            className={cx("story-subtitle", { "story-subtitle--readonly": isReadOnly })}
            data-test-id="story-subtitle">
            <TextArea
              onChange={handleSubHeadlineChange}
              placeholder={t("story-editor.header-card.subtitle")}
              value={story.subheadline}
              errorMessage={getErrorMessage(errors["subheadline"])}
              variant="plain"
              size="extra-large"
              disabled={isReadOnly}
            />
          </div>
        )}
        {!storyTemplateFields["hero-image-s3-key"].validations["hidden"] && (
          <div
            data-test-id="story-field-label-hero-image"
            className={cx(
              "story-field-label",
              "hero-image",
              `${errors["hero-image"] && errors["hero-image"]["key"] ? "has-error" : ""}`
            )}>
            <Image color="currentColor" />
          </div>
        )}
        {!storyTemplateFields["hero-image-s3-key"].validations["hidden"] && (
          <HeroImage
            image={story["hero-image"]}
            openMediaGallery={() => openMediaGallery(storyId)}
            updateImageData={updateStoryHeroImageData}
            deleteImage={deleteStoryHeroImage}
            errors={errors && errors["hero-image"]}
            openPhotoEditor={openPhotoEditor}
            onSetAlternativesButtonClick={() => openAlternativesInspector(storyId)}
            readOnly={isReadOnly}
            isFocusPointMandatory={isFocusPointMandatory}
          />
        )}

        {story["story-template"] === "review" && (
          <React.Fragment>
            <div
              className={cx(
                "story-field-label",
                "review",
                `${errors["review-rating"] || errors["review-title"] ? "has-error" : ""}`
              )}
              data-test-id="story-field-label-review">
              <div
                className={cx("title-icon", `${errors["review-rating"] || errors["review-title"] ? "has-error" : ""}`)}>
                <DoubleStar color="currentColor" />
              </div>
            </div>
            <ReviewFields reviewData={story.metadata} updateMetadata={updateMetadata} errors={errors} />
          </React.Fragment>
        )}

        {story["story-template"] === "news-elsewhere" && (
          <React.Fragment>
            <div
              className={cx("story-field-label", "reference-url", `${errors["reference-url"] ? "has-error" : ""}`)}
              data-test-id="story-field-label-reference-url">
              <div className={cx("title-icon", `${errors["reference-url"] ? "has-error" : ""}`)}>
                <ExternalUrl color="currentColor" />
              </div>
            </div>
            <div className={styles["story-reference-url-container"]} data-test-id="story-reference-url-container">
              <TextField
                label={t("story-editor.header-card.reference-url")}
                onChange={handleReferenceUrlChange}
                placeholder={t("story-editor.header-card.url")}
                value={story.metadata && story.metadata["reference-url"]}
                errorMessage={getErrorMessage(errors["reference-url"])}
              />
            </div>
          </React.Fragment>
        )}

        <CustomMetadata
          customMetadataFields={metadataFields}
          customMetadata={customMetadata}
          updateMetadata={updateMetadata}
        />
      </section>
    );
  }
);

function mapStateToProps(state): StateProps {
  return {
    story: state.storyEditor.story as Story,
    storyTemplateFields: state.storyEditor.storyTemplateFields,
    customMetadataFields: state.config.customMetadataFields,
    errors: state.storyEditor.ui.errors.editor,
    isStoryLocked: state.storyEditor.ui.isStoryLocked,
    isAlternativesEnabled: state.features.isAlternativeHeadlineAndImageEnabled,
    isFocusPointMandatory: get(
      state.storyEditor.storyTemplateFields,
      ["hero-image-focus-point", "validations", "mandatory"],
      false
    )
  };
}

function mapDispatchToProps(dispatch): DispatchProps {
  return {
    onChangeOfStory: (key, value) => dispatch({ type: actions.UPDATE_STORY, payload: { key, value } }),
    openMediaGallery: (storyId) => dispatch(navigate(STORY_EDITOR_HERO_IMAGE_GALLERY_PATH, { id: storyId })),
    openAlternativesInspector: (storyId) => dispatch(navigate(STORY_EDITOR_ALTERNATIVES_PATH, { id: storyId })),
    updateStoryHeroImageData: (image) => dispatch({ type: actions.UPDATE_HERO_IMAGE_DATA, payload: { image } }),
    deleteStoryHeroImage: () => dispatch({ type: actions.DELETE_HERO_IMAGE }),
    updateMetadata: (key, value) => dispatch({ type: actions.UPDATE_STORY_METADATA, payload: { key, value } }),
    openPhotoEditor: (image, imageAs, imageId = "") =>
      dispatch({ type: actions.OPEN_PHOTO_EDITOR, payload: { image, imageAs, imageId } })
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(HeaderCard);
